import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { format, formatDistanceToNow } from 'date-fns';
import {
  ArrowLeft,
  User,
  MapPin,
  Clock,
  Calendar,
  FileText,
  Phone,
  Mail,
  Globe,
} from 'lucide-react';

import { clientsApi } from '../api/clients.api';
import { contractsApi } from '@/features/contracts/api/contracts.api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { InfoField } from '@/components/common/InfoField';
import { ClientDetailHeader } from './ClientDetailHeader';

export function ClientDetailPage() {
  const { id } = useParams();
  const { t } = useTranslation();

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['client', id],
    queryFn: () => clientsApi.getById(id!),
    enabled: !!id,
    retry: 1,
  });

  const { data: contractsData } = useQuery({
    queryKey: ['contracts', 'client', id],
    queryFn: () => contractsApi.getAll({ clientId: Number(id), limit: 100 }),
    enabled: !!id,
  });

  const contracts = contractsData?.results || [];

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <div className="bg-white border-b border-slate-200 px-6 py-8">
          <div className="max-w-6xl mx-auto">
            <Skeleton className="h-4 w-24 mb-6" />
            <div className="flex items-center gap-6">
              <Skeleton className="h-16 w-16 rounded-full" />
              <div className="space-y-3">
                <Skeleton className="h-10 w-48" />
                <div className="flex gap-2">
                  <Skeleton className="h-6 w-24" />
                  <Skeleton className="h-6 w-20" />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="max-w-6xl mx-auto px-6 py-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <Skeleton className="h-48" />
            <Skeleton className="h-48" />
            <Skeleton className="h-48" />
          </div>
        </div>
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="min-h-screen bg-slate-50 p-6">
        <div className="max-w-6xl mx-auto">
          <div className="bg-white rounded-lg border border-slate-200 p-8 text-center">
            <div className="text-red-500 mb-4">
              <User className="h-12 w-12 mx-auto mb-2 opacity-50" />
              <p className="text-lg font-medium">
                {(error as Error)?.message || t('failedToLoadClient')}
              </p>
            </div>
            <Link to="/app/clients">
              <Button variant="outline">
                <ArrowLeft className="mr-2 h-4 w-4" />
                {t('backToClients')}
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const fullName = [data.lastname, data.name, data.middlename].filter(Boolean).join(' ');
  const isPerson = data.type === 'PERSON';
  const displayName = fullName || data.name || t('unnamed');

  return (
    <div className="min-h-screen bg-slate-50">
      <ClientDetailHeader
        data={data}
        displayName={displayName}
        isPerson={isPerson}
        contractCount={contracts?.length || 0}
      />

      <div className="max-w-6xl mx-auto px-6 py-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Contact Information Card */}
          <Card className="bg-white border-slate-200 shadow-sm hover:shadow-md transition-shadow">
            <CardHeader className="pb-3 border-b border-slate-100">
              <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                <User className="h-5 w-5 text-slate-400" />
                {t('contactInformation')}
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4 space-y-4">
              <InfoField icon={User} label={t('fullName')} value={displayName} />
              <InfoField
                icon={Phone}
                label={t('phone')}
                value={data.phone}
                href={data.phone ? `tel:${data.phone}` : undefined}
              />
              <InfoField
                icon={Mail}
                label={t('email')}
                value={data.email}
                href={data.email ? `mailto:${data.email}` : undefined}
              />
              <InfoField
                icon={Globe}
                label={t('website')}
                value={data.website}
                href={data.website ? (data.website.startsWith('http') ? data.website : `https://${data.website}`) : undefined}
              />
            </CardContent>
          </Card>

          {/* Address Card */}
          <Card className="bg-white border-slate-200 shadow-sm hover:shadow-md transition-shadow">
            <CardHeader className="pb-3 border-b border-slate-100">
              <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                <MapPin className="h-5 w-5 text-slate-400" />
                {t('address')}
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4 space-y-4">
              <InfoField icon={MapPin} label={t('street')} value={data.street} />
              <InfoField label={t('city')} value={data.city} />
              <InfoField label={t('region')} value={data.region} />
              <InfoField label={t('country')} value={data.country} />
              <InfoField label={t('zipCode')} value={data.zip} mono />
            </CardContent>
          </Card>

          {/* System Information Card */}
          <Card className="bg-white border-slate-200 shadow-sm hover:shadow-md transition-shadow">
            <CardHeader className="pb-3 border-b border-slate-100">
              <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                <Clock className="h-5 w-5 text-slate-400" />
                {t('systemInformation')}
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4 space-y-4">
              <InfoField label={t('clientId')} value={String(data.id)} mono />
              <InfoField
                icon={Calendar}
                label={t('createdAt')}
                value={
                  data.createdAt
                    ? format(new Date(data.createdAt), 'MMM d, yyyy HH:mm')
                    : '-'
                }
              />
              <InfoField
                label={t('updatedAt')}
                value={
                  data.updatedAt ? (
                    <span>
                      {format(new Date(data.updatedAt), 'MMM d, yyyy HH:mm')}
                      <span className="text-slate-400 text-xs ml-2">
                        ({formatDistanceToNow(new Date(data.updatedAt), { addSuffix: true })})
                      </span>
                    </span>
                  ) : (
                    '-'
                  )
                }
              />
              {data.createdById && (
                <InfoField label={t('createdById')} value={String(data.createdById)} mono />
              )}
            </CardContent>
          </Card>

          {/* Contracts Section */}
          <Card className="bg-white border-slate-200 shadow-sm hover:shadow-md transition-shadow md:col-span-2 lg:col-span-3">
            <CardHeader className="pb-3 border-b border-slate-100">
              <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                <FileText className="h-5 w-5 text-slate-400" />
                {t('contracts')}
                {contracts && contracts.length > 0 && (
                  <span className="ml-2 bg-blue-100 text-blue-700 text-xs font-semibold px-2 py-0.5 rounded-full">
                    {contracts.length}
                  </span>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4">
              {contracts && contracts.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {contracts.map((contract: any) => (
                    <Link
                      key={contract.id}
                      to={`/app/contracts/${contract.id}`}
                      className="block"
                    >
                      <div className="border border-slate-200 rounded-lg p-4 hover:border-blue-300 hover:bg-blue-50/50 transition-colors">
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-base font-bold text-slate-900">
                            {contract.name || `#${contract.id}`}
                          </span>
                          <Badge variant="outline" className="text-xs">
                            {t(`status.${contract.status || 'NEW'}`)}
                          </Badge>
                        </div>
                        <div className="space-y-2 text-sm">
                          <div className="flex items-center justify-between text-slate-600">
                            <span>{t('type')}</span>
                            <span className="font-medium text-slate-900">
                              {contract.type ? t(`status.${contract.type}`) : '-'}
                            </span>
                          </div>
                          <div className="flex items-center justify-between text-slate-600">
                            <span>{t('startDate')}</span>
                            <span className="font-medium text-slate-900">
                              {contract.startsAt
                                ? format(new Date(contract.startsAt), 'MMM d, yyyy')
                                : '-'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <FileText className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                  <p className="font-medium">{t('noContractsFound')}</p>
                  <Link to={`/app/contracts/action?clientId=${data.id}`}>
                    <Button variant="outline" className="mt-4">
                      {t('createFirstContract')}
                    </Button>
                  </Link>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
